import { Appearance, Linking, Platform } from 'react-native';
import InAppBrowser from 'utils/packages/inAppBrowser';
import { URL } from 'utils/packages/rnUrlPolyfill';
import StatusBar from 'design-system/StatusBar';
import Sentry from './sentry';
import { isWeb } from '../constants';
export const forceProtocol = (url) => {
    try {
        const parsed = new URL(url);
        return parsed.href;
    }
    catch {
        if (url.startsWith('http://')) {
            return url.replace('http://', 'https://');
        }
        if (!url.startsWith('https://')) {
            return `https://${url}`;
        }
        return url;
    }
};
/**
 * iOS workaround: Delay browser opening to prevent premature dismissal when
 * launched from push notifications (ACC-6000). The 300ms window allows the
 * notification UI to fully dismiss before presenting the in-app browser.
 */
const IOS_NOTIFICATION_BROWSER_DELAY_MS = 300;
const openLink = async (inputUrl, readerMode = false, withDelay = false) => {
    if (isWeb) {
        Linking.openURL(inputUrl).catch((err) => {
            Sentry.logError({ filename: inputUrl, message: err });
        });
    }
    else {
        const url = forceProtocol(inputUrl);
        if (await InAppBrowser.isAvailable()) {
            const systemIsDarkMode = Appearance.getColorScheme() === 'dark';
            const systemBarAlwaysWhite = Platform.OS === 'ios' && parseFloat(Platform.Version) >= 26.2;
            const style = StatusBar.pushStackEntryWithoutChecking({
                barStyle: systemIsDarkMode && !systemBarAlwaysWhite
                    ? 'light-content'
                    : 'dark-content',
            });
            try {
                if (withDelay) {
                    await new Promise((resolve) => {
                        setTimeout(resolve, IOS_NOTIFICATION_BROWSER_DELAY_MS);
                    });
                }
                await InAppBrowser.open(url, {
                    readerMode,
                    animated: true,
                    modalTransitionStyle: 'coverVertical',
                    modalEnabled: false,
                });
            }
            catch (error) {
                Sentry.logError({ filename: url, message: error.toString() });
            }
            finally {
                StatusBar.popStackEntry(style);
            }
        }
    }
};
export const knownLinks = {
    investTerms: 'https://emma-app.com/invest-terms',
    autosaveInfo: 'https://help.emma-app.com/en/article/what-is-an-autosave-cu6zz4/',
    fxFees: 'https://help.emma-app.com/en/article/what-is-the-emma-invest-fx-currency-conversion-fee-o9asm7/',
    cryptoLogin: 'https://help.emma-app.com/en/category/cryptocurrency-blockchain-ij01s7/',
    potsTnc: 'https://emma-app.com/pots-terms',
    privacy: 'https://emma-app.com/app-privacy',
    terms: 'https://emma-app.com/app-terms',
    creditScoreTerms: 'https://emma-app.com/credit-score-terms',
    facebook: 'https://www.facebook.com/emmafinance',
    twitter: 'https://twitter.com/emma_finance',
    instagram: 'https://www.instagram.com/emma_finance',
    tiktok: 'https://www.tiktok.com/@emma_finance',
    reddit: 'https://www.reddit.com/r/emmafinance/',
    emma: 'https://emma-app.com',
    jobs: 'https://emma-app.com/jobs',
    blog: 'https://emma-app.com/blog',
    community: 'https://community.emma-app.com/',
    communityBankIntegrations: 'https://community.emma-app.com/c/integrations-requests/32',
    help: 'https://help.emma-app.com/',
    appStore: 'https://itunes.apple.com/app/id1270062373',
    playStore: 'https://play.google.com/store/apps/details?id=com.emmaprod',
    playStoreSubscriptions: 'https://play.google.com/store/account/subscriptions',
    referral: 'https://help.emma-app.com/en/article/referral-program-terms-and-conditions-9whdkj/',
    smartRules: 'https://help.emma-app.com/en/article/smart-rules-6dfta8/',
    transactionCategories: 'https://help.emma-app.com/en/article/change-a-transaction-category-iipyy2/',
    howSafeIsEmmaApp: 'https://help.emma-app.com/en/article/how-safe-is-emma-app-kfmgsi/',
    whyDoINeedToConnectABank: 'https://help.emma-app.com/en/article/why-do-i-need-to-connect-a-bank-account-to-emma-bptg3b/',
    codeIsNotArriving: 'https://help.emma-app.com/en/article/my-phone-number-verification-code-is-not-arriving-bvzysw/',
    whatIsMyPasscode: 'https://help.emma-app.com/en/article/what-is-my-4-digit-passcode-neags0',
    signUpTroubleshooting: 'https://help.emma-app.com/en/article/signup-troubleshooting-121894n/',
    signInTroubleshooting: 'https://help.emma-app.com/en/article/signing-troubleshooting-17vc0m5/',
    privateRelayEmailHelp: 'https://help.emma-app.com/en/article/why-do-i-see-a-privaterelay-email-address-linked-to-my-emma-account-z55mhk/',
    pendingDepositHelp: 'https://help.emma-app.com/en/article/how-do-i-top-up-my-emma-investment-account-and-how-long-does-it-take-1js9sse/',
    amendAnalytics: 'https://help.emma-app.com/en/category/amend-your-analytics-155lckn/',
    depositFAQ: 'https://help.emma-app.com/en/article/how-long-does-it-take-to-deposit-money-in-emma-68xzrr/?bust=1675269981517',
    rentReportingTerms: 'http://emma-app.com/rent-reporting-terms',
    whyDoINeedToSelectLogins: 'https://help.emma-app.com/en/article/why-do-i-need-to-select-bank-logins-1iyl7pa/?bust=1681819634437',
    whatAreInactiveLogins: 'https://help.emma-app.com/en/article/what-are-inactive-bank-logins-yxqsnm/',
    withdrawalInfo: 'https://help.emma-app.com/en/article/how-long-do-pot-withdrawals-take-2rk4n3/',
    budgetingHelp: 'https://help.emma-app.com/en/category/budgeting-1s6usi4/',
    rollingBudgets: 'https://help.emma-app.com/en/article/rolling-budgets-6qct2p/',
    potsHelp: 'https://help.emma-app.com/en/category/emma-save-1q0zv9n/',
    spacesHelp: 'https://help.emma-app.com/en/article/emma-spaces-170x1at/',
    bankLoginsHelp: 'https://help.emma-app.com/en/category/accounts-18ix4l7/',
    pendingTxnHelp: 'https://help.emma-app.com/en/article/amend-pending-transactions-4wlf1y/',
    splitTxnHelp: 'https://help.emma-app.com/en/article/split-a-transaction-1jytjas/',
    groupsHelp: 'https://help.emma-app.com/en/category/pay-transfers-groups-9n3m27/',
    committedSpendingHelp: 'https://help.emma-app.com/en/article/learn-committed-spending-1cg2t8v/',
    rentReportingWhatCanWeReport: 'https://help.emma-app.com/en/article/what-can-be-included-in-rent-reporting-1ju8tch/',
    createCreditBuilderLink: 'https://start.credito.co.uk/emma/',
    noSubscriptions: 'https://help.emma-app.com/en/article/premium-plans-not-loading-kxl3gv/',
    paidPlanTncs: 'http://emma-app.com/paid-plans-terms ',
    kycProcess: 'https://help.emma-app.com/en/article/emma-kyc-process-10ui2bb/',
    roadMap: 'https://www.notion.so/emmafinance/Emma-Transparent-Product-Roadmap-e2844fc1e3174d20a04dcc1a4e85d2c9',
    unsettledCashInfo: 'https://help.emma-app.com/en/article/what-is-the-unsettled-cash-balance-in-emma-1jjhsuc/',
    webSignInHelp: 'https://help.emma-app.com/en/article/how-do-i-sign-in-to-the-emma-web-dashboard-tyh4i7/',
    balanceAndBudget: 'https://help.emma-app.com/en/article/why-is-my-left-of-different-from-my-available-balance-nklig0/',
    unknownMerchants: 'https://help.emma-app.com/en/article/what-are-unknown-merchants-in-analytics-tg8a8v/?bust=1725553312590',
    yapilyTerms: 'https://www.yapily.com/legal/end-user-terms',
    yapilyPrivacy: 'https://www.yapily.com/legal/privacy-policy',
    patternDayTrading: 'https://help.emma-app.com/en/article/what-is-pattern-day-trading-1bazbf2/',
    buyInShares: 'https://help.emma-app.com/en/article/how-does-buy-in-shares-work-3qkfvy/?bust=1737056751998',
    excludedTransactions: 'https://help.emma-app.com/en/article/exclude-a-transaction-oyflul/',
    loans: 'https://emma.monevo.co.uk/apply?campaign_code=cF1WawMLCwVHT3o',
    creditCards: 'https://emma.monevo.co.uk/apply?campaign_code=cF1WawMLCwVDSnY',
    whatIsRentReporting: 'https://help.emma-app.com/en/article/what-is-rent-reporting-mq3x33/',
    merchantBudgetsLearnMore: 'https://help.emma-app.com/en/article/what-are-merchant-budgets-qihqj9/',
    groupSettleUp: 'https://help.emma-app.com/en/article/how-do-i-settle-payments-in-a-group-diaurt/',
    mortgagesHabito: 'https://www.habito.com/?utm_source=emma&utm_medium=referral&utm_campaign=partnership',
    renewConnectionHelp: 'https://help.emma-app.com/en/article/renew-my-90-day-bank-access-cnzni5/',
    recurringPayments: 'https://help.emma-app.com/en/category/subscriptions-1xrbcm4/',
    refundEmma: 'https://help.emma-app.com/en/article/refund-emma-plus-emma-pro-or-emma-ultimate-1ka7v4r/',
    FSCSprotection: 'https://help.emma-app.com/en/article/how-fscs-protection-works-on-emma-interest-pots-1nyt9wi/',
    supportedAccountTypes: 'https://help.emma-app.com/en/article/what-type-of-accounts-are-supported-by-open-banking-1544pql/?bust=1774623191018',
    whatBanksDoYouSupportInTheUk: 'https://help.emma-app.com/en/article/what-banks-do-you-support-in-the-uk-tskl17/',
    whyCantIAddSavingsOrIsas: 'https://help.emma-app.com/en/article/why-cant-i-add-my-savings-or-isas-in-emma-1xb0unt/',
    howSafeIsEmmaAppUs: 'https://help.emma-app.com/en-us/article/how-safe-is-emma-app-16h0les/',
};
const openKnownLink = (link) => openLink(knownLinks[link]);
export default {
    openLink,
    openKnownLink,
    openKycProcessLink: () => openKnownLink('kycProcess'),
    openGroupSettleUp: () => openKnownLink('groupSettleUp'),
    openPotsTnc: () => openKnownLink('potsTnc'),
    openEmmaFacebook: () => openKnownLink('facebook'),
    openEmmaTwitter: () => openKnownLink('twitter'),
    openEmmaInstagram: () => openKnownLink('instagram'),
    openEmmaTiktok: () => openKnownLink('tiktok'),
    openEmmaReddit: () => openKnownLink('reddit'),
    openEmmaJobs: () => openKnownLink('jobs'),
    openEmmaBlog: () => openKnownLink('blog'),
    openEmmaCommunity: () => openKnownLink('community'),
    openEmmaRoadMap: () => openKnownLink('roadMap'),
    openEmmaCommunityBankIntegrations: () => openKnownLink('communityBankIntegrations'),
    openEmmaPrivacy: () => openKnownLink('privacy'),
    openEmmaTerms: () => openKnownLink('terms'),
    openEmmaHelp: () => openKnownLink('help'),
    openSupportedAccountTypes: () => openKnownLink('supportedAccountTypes'),
    openCryptoLoginInfo: () => openKnownLink('cryptoLogin'),
    openPendingDepositHelp: () => openKnownLink('pendingDepositHelp'),
    openEmmaReferral: () => openKnownLink('referral'),
    openSmartRules: () => openKnownLink('smartRules'),
    openManageSubscriptions: () => {
        if (Platform.OS === 'android') {
            Linking.openURL(knownLinks.playStoreSubscriptions);
        }
    },
    openEmmaInAppStore: () => openKnownLink('appStore'),
    openTransactionCategories: () => openKnownLink('transactionCategories'),
    openDepositFAQ: () => openKnownLink('depositFAQ'),
    openHowSafeIsEmmaApp: () => openKnownLink('howSafeIsEmmaApp'),
    openPrivateRelayEmailHelp: () => openKnownLink('privateRelayEmailHelp'),
    openRenewConnectionHelp: () => openKnownLink('renewConnectionHelp'),
    openRecurringPayments: () => openKnownLink('recurringPayments'),
    openRefundEmma: () => openKnownLink('refundEmma'),
};
